// Fases in lagen: in Planner een samenvattende taak, die zelf weer onder een fase kan zitten. Hier het pad boven een
// taak, de taken per fase, en de fasekaarten op het bord (ontwerp "Fases op het bord", 20/10/2026):
// - een fase is een kaart met de taken van de kolom erin; losse taken en fasekaarten staan samen op urgentie, een
//   fasekaart staat waar haar eerste (dringendste) taak zou staan;
// - een laag zonder eigen taken in de kolom, met één subfase, schuift in de grijze regel ("Migratie naar Qargo › To
//   Qargo" boven "Orders");
// - hoogstens MAX_NEST fasekaarten in elkaar; een diepere fase komt als eigen kaart ernaast, met haar pad;
// - het label van een fase telt niet mee voor de categorie van haar taken (zie faseParked voor Opschonen).
import type { FaseProject } from "./api-clockify";
import { categoryOf } from "./planning";
import type { Fase, Task } from "./types";

/**
 * Het Clockify-project dat voor een taak of fase geldt: de dichtstbijzijnde fase erboven (of de fase zelf) met een
 * koppeling. Zonder: null, en dan geldt het project van het klantplan. `from` is faseId van een taak, of de fase zelf.
 */
export function faseProjectOf(from: string | null | undefined, fases: Map<string, Fase>, faseProjects: Record<string, FaseProject> | undefined): { fase: Fase; project: FaseProject } | null {
  if (!faseProjects) return null;
  const seen = new Set<string>();
  let f = from ? fases.get(from) : undefined;
  while (f && !seen.has(f.id)) {
    seen.add(f.id);
    const p = faseProjects[f.id];
    if (p) return { fase: f, project: p };
    f = f.parentId ? fases.get(f.parentId) : undefined;
  }
  return null;
}

/** Een fase in de vorm van een taak, voor de onderdelen die lade en venster delen (labels, checklist, bijlagen, verloop). */
export function faseAsTask(f: Fase): Task {
  return {
    id: f.id, projectId: f.projectId, bucketId: f.bucketId, title: f.title, start: f.start, end: f.end, progress: f.progress,
    priority: 5, critical: false, milestone: false, faseId: f.parentId, order: f.order, labels: f.labels, owners: [],
    checklist: f.checklist || [], attachments: f.attachments || [], notes: f.notes || "", conversation: f.conversation,
    modified: f.modified || "", created: f.created, pending: f.pending,
  };
}

/** De fases boven een taak of fase, van bovenaan tot de directe fase. */
export function faseChain(faseId: string | null | undefined, fases: Map<string, Fase>): Fase[] {
  const out: Fase[] = [];
  const seen = new Set<string>(); // een lus in de data mag niet blijven hangen
  let f = faseId ? fases.get(faseId) : undefined;
  while (f && !seen.has(f.id)) {
    seen.add(f.id);
    out.unshift(f);
    f = f.parentId ? fases.get(f.parentId) : undefined;
  }
  return out;
}

export const padText = (chain: { title: string }[]) => chain.map((f) => f.title).join(" › ");

/** Het pad, vooraan ingekort met "…" als het langer is dan max tekens; de directe fase blijft altijd staan. */
export function padShort(chain: { title: string }[], max = 60): string {
  const parts = chain.map((f) => f.title);
  let s = parts.join(" › ");
  while (s.length > max && parts.length > 1) {
    parts.shift();
    s = "… › " + parts.join(" › ");
  }
  return s;
}

/** Per fase de taken eronder, ook die in subfases. */
export function tasksByFase(tasks: Task[], fases: Map<string, Fase>): Map<string, Task[]> {
  const out = new Map<string, Task[]>();
  for (const t of tasks) {
    for (const f of faseChain(t.faseId, fases)) {
      const l = out.get(f.id);
      if (l) l.push(t);
      else out.set(f.id, [t]);
    }
  }
  return out;
}

/** De fase in het pad die in Planner zelf op On hold of Waiting staat (de eerste van bovenaan), of null. */
export const faseParked = (chain: Fase[]) => chain.find((f) => categoryOf(f.labels) === "geparkeerd") || null;

// ---------- fasekaarten ----------

export interface FaseBlock {
  kind: "fase";
  fase: Fase;
  /** grijze regel: de lagen erboven die niet als eigen kaart staan; in een lijst de klant vooraan */
  pad: string[];
  /** alle taken van deze kaart, ook die van de kaarten erin of ernaast, in volgorde */
  tasks: Task[];
  children: Block[];
  /** bovenaan in de groep: daar staat hoeveel taken bij collega's liggen */
  top: boolean;
}
export type Block = { kind: "task"; task: Task } | FaseBlock;

/** Hoogstens zoveel fasekaarten in elkaar. In de data van 20/10/2026 zit geen kaart dieper dan twee. */
export const MAX_NEST = 2;

interface Node { fase: Fase | null; items: ({ task: Task } | { node: Node })[]; tasks: Task[] }

/**
 * De taken van één groep (klant, bucket of lijst), in de volgorde waarin ze moeten staan, als blokken: losse taken en
 * fasekaarten. prefix zet iets vooraan in de grijze regel van een kaart bovenaan (de klant in Geparkeerd en Afsluiten).
 */
export function faseBlocks(tasks: Task[], fases: Map<string, Fase>, opts: { prefix?: (t: Task) => string; maxNest?: number } = {}): Block[] {
  const root: Node = { fase: null, items: [], tasks: [] };
  for (const t of tasks) {
    let node = root;
    root.tasks.push(t);
    for (const f of faseChain(t.faseId, fases)) {
      let next = node.items.find((i): i is { node: Node } => "node" in i && i.node.fase?.id === f.id)?.node;
      if (!next) {
        next = { fase: f, items: [], tasks: [] };
        node.items.push({ node: next });
      }
      next.tasks.push(t);
      node = next;
    }
    node.items.push({ task: t });
  }
  const max = opts.maxNest ?? MAX_NEST;
  const card = (node: Node, pad: string[], depth: number, top: boolean): Block[] => {
    let n = node;
    const segs = [...pad];
    while (n.items.length === 1 && "node" in n.items[0]) {
      segs.push(n.fase!.title);
      n = n.items[0].node;
    }
    const children: Block[] = [], after: Block[] = [];
    for (const it of n.items) {
      if ("task" in it) children.push({ kind: "task", task: it.task });
      else if (depth < max) children.push(...card(it.node, [], depth + 1, false));
      else after.push(...card(it.node, [...segs, n.fase!.title], depth, false));
    }
    return [{ kind: "fase", fase: n.fase!, pad: segs, tasks: n.tasks, children, top }, ...after];
  };
  return root.items.flatMap((it): Block[] => ("task" in it ? [{ kind: "task", task: it.task }] : card(it.node, opts.prefix ? [opts.prefix(it.node.tasks[0])] : [], 1, true)));
}
